import { issueBadgeClasses } from './badgeBase';
import { clsx } from 'clsx';
import { HugeiconsIcon } from '@hugeicons/react';
import { Bug01Icon, LayoutTwoColumnIcon, Alert01Icon, PaintBoardIcon, ArrowUp01Icon, Comment01Icon } from '@hugeicons/core-free-icons';
import { ISSUE_TYPE_GLYPHS, ISSUE_TYPE_SHORT_LABELS, inlineIconifyGlyph, type IssueTypeGlyph, type IssueTypeValue } from '@overlayqa/shared';
import type { IconSvgElement } from '@hugeicons/react';

export interface TypeVisual {
  label: string;
  bg: string;
  text: string;
  icon?: IconSvgElement;
  iconifyIcon?: string;
}

/**
 * The Hugeicons imports this surface can draw, by the name the shared glyph
 * map uses. WHICH glyph a type gets is decided once, in @overlayqa/shared
 * (ISSUE_TYPE_GLYPHS, 2026-09-02: one set on every surface, the surface only
 * decides whether a word sits beside it; here it does). TypeBadge.test.tsx
 * pins registry and map together, and resolve() throws at module load on a
 * name with no import, so a new glyph can never fall back to the grey pill.
 */
export const HUGEICON_REGISTRY: Record<string, IconSvgElement> = {
  Bug01Icon,
  LayoutTwoColumnIcon,
  Alert01Icon,
  PaintBoardIcon,
  ArrowUp01Icon,
  Comment01Icon,
};

// Paint is local: the tokens are this surface's own. Labels come from the
// shared vocabulary (2026-09-02) and glyphs from the shared map (same day).
// Keep each entry on one line as `label: L[...], bg: '...', text: '...'`:
// styles/__tests__/badgeContrast.source.test.ts reads that exact shape to
// measure every pairing's contrast, and an entry it cannot read is a pairing
// nobody measures.
const L = ISSUE_TYPE_SHORT_LABELS;
const TYPE_STYLE: Record<IssueTypeValue, { label: string; bg: string; text: string }> = {
  'design-bug': { label: L['design-bug'], bg: 'bg-[var(--color-type-bug-bg)]', text: 'text-[var(--color-type-bug)]' },
  'design-gap': { label: L['design-gap'], bg: 'bg-[var(--color-type-gap-bg)]', text: 'text-[var(--color-type-gap)]' },
  'design-debt': { label: L['design-debt'], bg: 'bg-[var(--color-type-debt-bg)]', text: 'text-[var(--color-type-debt)]' },
  accessibility: { label: L.accessibility, bg: 'bg-[var(--color-type-a11y-bg)]', text: 'text-[var(--color-type-a11y)]' },
  improvement: { label: L.improvement, bg: 'bg-[var(--color-type-improvement-bg)]', text: 'text-[var(--color-type-improvement)]' },
  general: { label: L.general, bg: 'bg-[var(--color-type-general-bg)]', text: 'text-[var(--color-type-general)]' },
  // Created by the design-system audit (server routes/tokenAudit.ts:202). Had no
  // entry here until 2026-08-04, so it rendered through the grey fallback below
  // while every other type got its color. Color mirrors the extension's Design
  // Tokens workflow (content/workflows/workflowColors.ts).
  'design-token': { label: L['design-token'], bg: 'bg-[var(--color-type-token-bg)]', text: 'text-[var(--color-type-token)]' },
};

function resolveGlyph(type: string, glyph: IssueTypeGlyph): Pick<TypeVisual, 'icon' | 'iconifyIcon'> {
  if (glyph.library === 'iconify') return { iconifyIcon: glyph.name };
  const icon = HUGEICON_REGISTRY[glyph.name];
  if (!icon) {
    throw new Error(`TypeBadge: ${type} names hugeicons "${glyph.name}" but HUGEICON_REGISTRY has no import for it`);
  }
  return { icon };
}

const TYPE_CONFIG: Record<string, TypeVisual> = Object.fromEntries(
  (Object.entries(ISSUE_TYPE_GLYPHS) as [IssueTypeValue, IssueTypeGlyph][]).map(([type, glyph]) => [
    type,
    { ...TYPE_STYLE[type], ...resolveGlyph(type, glyph) },
  ]),
);

/**
 * The glyph + colour an issue type is drawn with, or undefined for a type this
 * badge has no entry for (which is why the badge itself has a grey fallback).
 *
 * Exported so the issues-table screenshot placeholder (IssueThumbnail) paints
 * the SAME glyph this badge does. 45% of real production issues carry no
 * screenshot, so that placeholder is on screen constantly; a second hand-typed
 * type→icon map would drift from this one the first time a type is added, the
 * way `design-token` rendered through the grey fallback for months.
 */
export function getTypeVisual(type: string | undefined): TypeVisual | undefined {
  if (!type) return undefined;
  return TYPE_CONFIG[type];
}

/**
 * An Iconify-named glyph, painted from the shared inline copy at first render.
 * @iconify/react would fetch the name from api.iconify.design and paint an
 * empty svg until it answered, which left the Accessibility badge blank on the
 * issues table (Emily, 2026-09-02, 6.b). Every glyph in the shared issue-type
 * map ships inline. Shared with IssueThumbnail so both paint the same glyph.
 */
export function IconifyGlyph({ name, className }: { name: string; className: string }) {
  const inline = inlineIconifyGlyph(name);
  if (!inline) throw new Error(`TypeBadge: missing shared inline glyph ${name}`);
  return <svg viewBox={inline.viewBox} className={className} aria-hidden="true" dangerouslySetInnerHTML={{ __html: inline.body }} />;
}

interface TypeBadgeProps {
  type?: string;
}

export function TypeBadge({ type }: TypeBadgeProps) {
  if (!type) return null;
  const config = TYPE_CONFIG[type];
  if (!config) {
    return (
      <span className={clsx(issueBadgeClasses, 'bg-gray-100 text-gray-500')}>
        {type.replace('design-', '')}
      </span>
    );
  }
  return (
    <span className={clsx(issueBadgeClasses, config.bg, config.text)}>
      {config.icon ? (
        <HugeiconsIcon icon={config.icon} size={10} strokeWidth={2} color="currentColor" className="w-[10px] h-[10px] flex-shrink-0" aria-hidden="true" />
      ) : config.iconifyIcon ? (
        <IconifyGlyph name={config.iconifyIcon} className="w-[10px] h-[10px] flex-shrink-0" />
      ) : null}
      {config.label}
    </span>
  );
}
